/* @pareto-engineering/generator-front 1.0.12 */
import React, { useMemo } from 'react'

import {
  useLocation,
} from 'react-router-dom'

import {
  useApp,
  Page,
  AnimatedGradient,
} from '@pareto-engineering/design-system'

import {
  ConversionPage,
  aiSectionMap,
  NavigationBar,
  Footer,
  CaseStudyHeading,
  SectionCTA,
} from 'modules/components'

import PropTypes from 'prop-types'

import styleNames from '@pareto-engineering/bem/exports'

import './styles.scss'

// Local Definitions

const baseClassName = styleNames.base

const componentClassName = 'case-study-single'

/**
 * This is the component description.
 */
const Single = ({
  id,
  className:userClassName,
  style,
  children,
  jsonld,
  ...otherProps
}) => {
  const {
    // Header props
    title,
    subtitle,
    initialProp,
    initialValue,
    secondaryProp,
    secondaryValue,
    mainImage,

    // Meta props
    metaTitle,
    metaDescription,

    // Conversion form props
    mobileButtonText,
    desktopLaunchText,
    mobileLaunchText,

    // CTA Section props
    ctaTitle,
    ctaDescription,
    ctaButtonText,

    // Background props
    Background,
  } = otherProps

  const { SITE } = useApp()

  const { pathname } = useLocation()

  const helmetProps = useMemo(() => ({
    title           :metaTitle || title,
    meta_description:metaDescription || subtitle,
    canonical       :pathname,
    og_type         :'website',
    main_image      :mainImage || `${SITE.CANONICAL}/misc/social-share.jpg`,
    jsonld,
  }), [pathname])

  return (
    <ConversionPage
      id={id}
      className={[
        baseClassName,
        componentClassName,
        userClassName,
      ]
        .filter((e) => e)
        .join(' ')}
      style={style}
      helmetProps={helmetProps}
      Background={Background}
      conversionFormConfig={aiSectionMap}
      mobileButtonText={mobileButtonText}
      desktopLaunchText={desktopLaunchText}
      mobileLaunchText={mobileLaunchText}
    >
      <NavigationBar />

      <Page.Section
        id="case-study-header"
        className="to-plain"
        isSpaced
      >
        <CaseStudyHeading
          className="md-span-10 md-start-3 span-8"
          title={title}
          subtitle={subtitle}
          initialProp={initialProp}
          initialValue={initialValue}
          secondaryProp={secondaryProp}
          secondaryValue={secondaryValue}
          mainImage={mainImage}
        />
      </Page.Section>

      <Page.Section
        id="case-study-content-section"
        className="case-study-content x-background-far b-x"
      >
        <div className="span-8 md-start-4 md-span-8">
          {children}
        </div>
      </Page.Section>

      { ctaTitle ? (
        <SectionCTA
          className="from-plain"
          heading={
            (
              <span className="x-highlighted c-x">
                {ctaTitle}
              </span>
            )
          }
          subtitle={ctaDescription}
          buttonText={ctaButtonText}
        />
      ) : <div className="spacing from-plain" />}

      <Footer />
    </ConversionPage>
  )
}

Single.propTypes = {
  /**
   * The HTML id for this element
   */
  id:PropTypes.string,

  /**
   * The HTML class names for this element
   */
  className:PropTypes.string,

  /**
   * The React-written, css properties for this element.
   */
  style:PropTypes.objectOf(PropTypes.string),

  /**
   *  The children JSX
   */
  children:PropTypes.node,

  /**
   * The background of the page
   */
  Background:PropTypes.node,

  /**
   * The JSON-LD data for this page
   */
  // eslint-disable-next-line react/forbid-prop-types
  jsonld:PropTypes.objectOf(PropTypes.any),

  /**
   * The mobile launch text for the conversion form
   */
  mobileLaunchText:PropTypes.string,

  /**
   * The desktop launch text for the conversion form
   */
  desktopLaunchText:PropTypes.string,

  /**
   * The mobile button text for the conversion form
   */
  mobileButtonText:PropTypes.string,
}

Single.defaultProps = {
  Background       :AnimatedGradient,
  mobileButtonText :'Get started',
  mobileLaunchText :'Harness world-class AI talent.',
  desktopLaunchText:'Join leading AI companies in harnessing world-class AI talent.',
}

export default Single
